import { z } from "zod";

import { ValueConstraintSchema } from "./valueConstraint.schema";

/**
 * A single parameter policy within an action entry.
 *
 * Two modes only (CEO directive — simplified from 5 access levels):
 *
 * **Enabled** (`enabled: true`)
 *   Consumer sets the value freely (pass-through).
 *
 * **Disabled** (`enabled: false`)
 *   Card creator controls. The `value` field determines behavior:
 *   - Absent/empty → parameter is stripped (not sent to the API)
 *   - Static text → fixed value, always injected
 *   - Text with @variables → template resolved at execution
 *     (e.g. "FOLLOW UP: @mail_subject")
 *
 * The policy is keyed by a `ParameterPath`, so it addresses one position in the
 * parameters — `to`, `body.html`, `body.attachments[*].filename`. A path with no `.`
 * and no `[*]` is a plain top-level parameter and behaves exactly as it always has.
 */
export const ParameterPolicySchema = z.object({
  /** Whether the consumer can set this parameter freely */
  enabled: z.boolean(),
  /**
   * Value for disabled parameters.
   * - Omitted or empty string → parameter stripped from API call
   * - Static string → injected as-is (fixed value)
   * - String with @refs → template resolved against card variables at execution
   */
  value: z.string().optional(),
  /**
   * What the value at this path is allowed to be.
   *
   * Checked against the consumer's value when the parameter is enabled, and against
   * the FULLY SUBSTITUTED result when the parameter is a pinned template. The second
   * check is the one that refuses a pinned `to: "support+@ticket_id@company.com"`
   * once `ticket_id` has smuggled a comma and a second address into it.
   */
  constraint: ValueConstraintSchema.optional()
});

export type ParameterPolicy = z.infer<typeof ParameterPolicySchema>;
